import { useState, useEffect } from 'react';

interface OnboardingData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  partnerName: string;
  weddingDate: string;
  notes: string;
  dressSize: string;
  budgetMin: number;
  budgetMax: number;
  preferredStyles: string[];
  preferredBrands: string[];
  inspirationImages: string[];
  eventLocation: string;
}

const STORAGE_KEY = 'qr_onboarding_data';
const STEP_KEY = 'qr_onboarding_step';

export function useOnboardingPersistence(customerId?: string) {
  const [formData, setFormData] = useState<OnboardingData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    partnerName: "",
    weddingDate: "",
    notes: "",
    dressSize: "",
    budgetMin: 1000,
    budgetMax: 5000,
    preferredStyles: [],
    preferredBrands: [],
    inspirationImages: [],
    eventLocation: "",
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [hasRestoredData, setHasRestoredData] = useState(false);

  // Load persisted data on mount
  useEffect(() => {
    const savedData = localStorage.getItem(`${STORAGE_KEY}_${customerId}`);
    const savedStep = localStorage.getItem(`${STEP_KEY}_${customerId}`);
    
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setFormData(parsed);
        setHasRestoredData(true);
      } catch (error) {
        console.error('Error parsing saved data:', error);
      }
    }
    
    if (savedStep) {
      const step = parseInt(savedStep, 10);
      if (step >= 1 && step <= 4) {
        setCurrentStep(step);
      }
    }
  }, [customerId]);

  // Save data whenever form data changes
  useEffect(() => {
    if (customerId) {
      localStorage.setItem(`${STORAGE_KEY}_${customerId}`, JSON.stringify(formData));
    }
  }, [formData, customerId]);

  // Save step whenever it changes
  useEffect(() => {
    if (customerId) {
      localStorage.setItem(`${STEP_KEY}_${customerId}`, currentStep.toString());
    }
  }, [currentStep, customerId]);

  const updateFormData = (updates: Partial<OnboardingData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const clearPersistedData = () => {
    if (customerId) {
      localStorage.removeItem(`${STORAGE_KEY}_${customerId}`);
      localStorage.removeItem(`${STEP_KEY}_${customerId}`);
    }
    setHasRestoredData(false);
  };

  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      partnerName: "",
      weddingDate: "",
      notes: "",
      dressSize: "",
      budgetMin: 1000,
      budgetMax: 5000,
      preferredStyles: [],
      preferredBrands: [],
      inspirationImages: [],
      eventLocation: "",
    });
    setCurrentStep(1);
    clearPersistedData();
  };

  return {
    formData,
    setFormData,
    updateFormData,
    currentStep,
    setCurrentStep,
    hasRestoredData,
    clearPersistedData,
    resetForm,
  };
}